import { useState } from "react";
import clsx from "clsx";
import { Markdown } from "@/ui/atoms/Markdown/Markdown";

interface ExpandableMarkdownProps {
    text: string;
    maxLength?: number;
    className?: string;
}

/**
 * Checks if text is a JSON response with results array and extracts values
 * Handles format: {"results":[{"value":"text1"},{"value":"text2"}]}
 */
const extractResultsValues = (text: string): string | null => {
    try {
        const trimmed = text.trim();
        if (!trimmed.startsWith("{") || !trimmed.includes("results")) {
            return null;
        }

        const parsed = JSON.parse(trimmed);

        if (
            parsed &&
            typeof parsed === "object" &&
            Array.isArray(parsed.results) &&
            parsed.results.length > 0
        ) {
            const hasValues = parsed.results.every(
                (item: unknown) =>
                    item &&
                    typeof item === "object" &&
                    "value" in item &&
                    typeof item.value === "string",
            );

            if (hasValues) {
                const values = parsed.results.map(
                    (item: { value: string }) => item.value,
                );
                return values
                    .map((val, idx) => `${idx + 1}. ${val}`)
                    .join("\n\n");
            }
        }

        return null;
    } catch {
        return null;
    }
};

export const ExpandableMarkdown = ({
    text,
    maxLength = 500,
    className,
}: ExpandableMarkdownProps) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!text) {
        return <span className={clsx(className, "body-s subdued")}>-</span>;
    }

    const processedText = extractResultsValues(text) || text;
    const shouldTruncate = processedText.length > maxLength;

    if (!shouldTruncate) {
        return (
            <div className={className}>
                <Markdown>{processedText}</Markdown>
            </div>
        );
    }

    const displayText = isExpanded
        ? processedText
        : `${Array.from(processedText).slice(0, maxLength).join("")}...`;

    return (
        <div className={className}>
            <Markdown>{displayText}</Markdown>
            <span
                role="button"
                onClick={() => setIsExpanded(!isExpanded)}
                tabIndex={0}
                className="text-link ml-1 cursor-pointer"
            >
                {isExpanded ? "Show less" : "See more"}
            </span>
        </div>
    );
};
